import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Code,
  Copy,
  Check,
  Phone,
  FileText,
  ExternalLink,
  Palette,
  Settings,
  Eye,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const EmbedFormsPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [copied, setCopied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  
  // Form customization
  const [formType, setFormType] = useState('callback'); // 'callback' or 'quote'
  const [formConfig, setFormConfig] = useState({
    title: '',
    description: '',
    buttonText: '',
    primaryColor: theme?.colors?.accent || '#00d4aa',
    backgroundColor: '#ffffff',
    borderRadius: '12',
    showPhone: true,
    showEmail: true,
    showMessage: true,
    showCompany: false,
    showProjectType: true,
    showDeadline: true,
    capturePageUrl: true,
    successMessage: 'Votre demande a bien été envoyée. Nous vous recontacterons rapidement.'
  });
  
  // Project types for quote form
  const projectTypes = [
    { value: 'site_web', label: 'Site Web' },
    { value: 'application_mobile', label: 'Application Mobile' },
    { value: 'identite_visuelle', label: 'Identité Visuelle / Logo' },
    { value: 'video', label: 'Vidéo / Motion Design' },
    { value: 'marketing', label: 'Marketing Digital' },
    { value: 'reseaux_sociaux', label: 'Réseaux Sociaux' },
    { value: 'print', label: 'Print / Supports imprimés' },
    { value: 'autre', label: 'Autre' }
  ];
  
  // Deadline options for quote form
  const deadlineOptions = [
    { value: 'urgent', label: 'Urgent (< 1 semaine)' },
    { value: '2_semaines', label: '2 semaines' },
    { value: '1_mois', label: '1 mois' },
    { value: '2_mois', label: '2 mois' },
    { value: '3_mois', label: '3 mois ou plus' },
    { value: 'flexible', label: 'Flexible / Pas de deadline' }
  ];
  
  // Use environment variable, or fallback to current window location (auto-detect domain)
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  useEffect(() => {
    fetchAgencyInfo();
  }, []);
  
  useEffect(() => {
    // Update default values based on form type - force update title/description/button for the selected type
    if (formType === 'callback') {
      setFormConfig(prev => ({
        ...prev,
        title: 'Demande de rappel',
        description: 'Laissez-nous vos coordonnées, nous vous rappelons rapidement.',
        buttonText: 'Être rappelé',
        showMessage: false,
        showProjectType: false,
        showDeadline: false
      }));
    } else {
      setFormConfig(prev => ({
        ...prev,
        title: 'Demande de devis',
        description: 'Décrivez votre projet, nous vous enverrons un devis personnalisé.',
        buttonText: 'Envoyer ma demande',
        showMessage: true,
        showProjectType: true,
        showDeadline: true
      }));
    }
  }, [formType]);
  
  const fetchAgencyInfo = async () => {
    try {
      const res = await api.get('/settings/agency-info');
      setAgencyInfo(res.data);
    } catch (error) {
      console.error('Error fetching agency info:', error);
    }
  };
  
  const generateEmbedCode = () => {
    const config = {
      type: formType,
      ...formConfig,
      apiEndpoint: `${backendUrl}/api/leads/embed`
    };
    
    const configString = JSON.stringify(config);
    const encodedConfig = btoa(encodeURIComponent(configString));
    
    // Inline script that creates the form
    const embedCode = `<!-- MSC Lead Form - ${formType === 'callback' ? 'Demande de rappel' : 'Demande de devis'} -->
<div id="msc-lead-form-${formType}"></div>
<script>
(function() {
  var config = JSON.parse(decodeURIComponent(atob('${encodedConfig}')));
  var container = document.getElementById('msc-lead-form-${formType}');
  if (!container) return;
  
  var style = document.createElement('style');
  style.textContent = \`
    .msc-form-container {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      max-width: 400px;
      margin: 0 auto;
      padding: 24px;
      background: \${config.backgroundColor};
      border-radius: \${config.borderRadius}px;
      box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
    }
    .msc-form-title {
      font-size: 20px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 8px;
    }
    .msc-form-desc {
      font-size: 14px;
      color: #666;
      margin-bottom: 20px;
    }
    .msc-form-group {
      margin-bottom: 16px;
    }
    .msc-form-label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: #374151;
      margin-bottom: 6px;
    }
    .msc-form-input, .msc-form-textarea {
      width: 100%;
      padding: 10px 14px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 14px;
      transition: border-color 0.2s;
      box-sizing: border-box;
    }
    .msc-form-input:focus, .msc-form-textarea:focus {
      outline: none;
      border-color: \${config.primaryColor};
      box-shadow: 0 0 0 3px \${config.primaryColor}20;
    }
    .msc-form-textarea {
      min-height: 100px;
      resize: vertical;
    }
    .msc-form-btn {
      width: 100%;
      padding: 12px 20px;
      background: \${config.primaryColor};
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s;
    }
    .msc-form-btn:hover {
      opacity: 0.9;
    }
    .msc-form-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .msc-form-success {
      text-align: center;
      padding: 20px;
    }
    .msc-form-success svg {
      width: 48px;
      height: 48px;
      color: \${config.primaryColor};
      margin-bottom: 12px;
    }
    .msc-form-error {
      background: #fef2f2;
      color: #dc2626;
      padding: 10px;
      border-radius: 6px;
      font-size: 14px;
      margin-bottom: 16px;
    }
    .msc-form-select {
      width: 100%;
      padding: 10px 14px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 14px;
      transition: border-color 0.2s;
      box-sizing: border-box;
      background: white;
      cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 12px center;
    }
    .msc-form-select:focus {
      outline: none;
      border-color: \${config.primaryColor};
      box-shadow: 0 0 0 3px \${config.primaryColor}20;
    }
  \`;
  document.head.appendChild(style);
  
  // Project type options
  var projectTypeOptions = [
    { value: '', label: 'Sélectionnez un type de projet' },
    { value: 'site_web', label: 'Site Web' },
    { value: 'application_mobile', label: 'Application Mobile' },
    { value: 'identite_visuelle', label: 'Identité Visuelle / Logo' },
    { value: 'video', label: 'Vidéo / Motion Design' },
    { value: 'marketing', label: 'Marketing Digital' },
    { value: 'reseaux_sociaux', label: 'Réseaux Sociaux' },
    { value: 'print', label: 'Print / Supports imprimés' },
    { value: 'autre', label: 'Autre' }
  ];
  
  // Deadline options
  var deadlineOptions = [
    { value: '', label: 'Sélectionnez un délai' },
    { value: 'urgent', label: 'Urgent (< 1 semaine)' },
    { value: '2_semaines', label: '2 semaines' },
    { value: '1_mois', label: '1 mois' },
    { value: '2_mois', label: '2 mois' },
    { value: '3_mois', label: '3 mois ou plus' },
    { value: 'flexible', label: 'Flexible / Pas de deadline' }
  ];
  
  // Generate select HTML
  function generateSelectHtml(name, options, required) {
    var optionsHtml = options.map(function(opt) {
      return '<option value="' + opt.value + '">' + opt.label + '</option>';
    }).join('');
    return '<select name="' + name + '" class="msc-form-select"' + (required ? ' required' : '') + '>' + optionsHtml + '</select>';
  }
  
  var formHtml = \`
    <div class="msc-form-container">
      <div class="msc-form-title">\${config.title}</div>
      <div class="msc-form-desc">\${config.description}</div>
      <form id="msc-lead-form-inner-${formType}" onsubmit="return false;">
        <div class="msc-form-group">
          <label class="msc-form-label">Nom complet *</label>
          <input type="text" name="name" class="msc-form-input" required placeholder="Jean Dupont">
        </div>
        \${config.showPhone ? '<div class="msc-form-group"><label class="msc-form-label">Téléphone *</label><input type="tel" name="phone" class="msc-form-input" required placeholder="06 12 34 56 78"></div>' : ''}
        \${config.showEmail ? '<div class="msc-form-group"><label class="msc-form-label">Email</label><input type="email" name="email" class="msc-form-input" placeholder="email@exemple.com"></div>' : ''}
        \${config.showCompany ? '<div class="msc-form-group"><label class="msc-form-label">Société</label><input type="text" name="company" class="msc-form-input" placeholder="Nom de votre société"></div>' : ''}
        \${config.showProjectType ? '<div class="msc-form-group"><label class="msc-form-label">Type de projet *</label>' + generateSelectHtml('project_type', projectTypeOptions, true) + '</div>' : ''}
        \${config.showDeadline ? '<div class="msc-form-group"><label class="msc-form-label">Délai de livraison souhaité</label>' + generateSelectHtml('deadline', deadlineOptions, false) + '</div>' : ''}
        \${config.showMessage ? '<div class="msc-form-group"><label class="msc-form-label">Votre message *</label><textarea name="message" class="msc-form-textarea" required placeholder="Décrivez votre projet..."></textarea></div>' : ''}
        <input type="hidden" name="page_url" value="">
        <input type="hidden" name="form_type" value="${formType}">
        <div id="msc-form-error-${formType}" class="msc-form-error" style="display:none;"></div>
        <button type="submit" class="msc-form-btn">\${config.buttonText}</button>
      </form>
      <div id="msc-form-success-${formType}" class="msc-form-success" style="display:none;">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        <div style="font-size:18px;font-weight:600;color:#1a1a1a;margin-bottom:8px;">Merci !</div>
        <div style="color:#666;">\${config.successMessage}</div>
      </div>
    </div>
  \`;
  container.innerHTML = formHtml;
  
  // Set page URL
  var pageUrlInput = container.querySelector('input[name="page_url"]');
  if (pageUrlInput && config.capturePageUrl) {
    pageUrlInput.value = window.location.href;
  }
  
  // Form submission
  var form = document.getElementById('msc-lead-form-inner-${formType}');
  form.addEventListener('submit', function(e) {
    e.preventDefault();
    var btn = form.querySelector('button');
    var errorDiv = document.getElementById('msc-form-error-${formType}');
    var successDiv = document.getElementById('msc-form-success-${formType}');
    
    btn.disabled = true;
    btn.textContent = 'Envoi en cours...';
    errorDiv.style.display = 'none';
    
    var formData = new FormData(form);
    var data = {};
    formData.forEach(function(value, key) { data[key] = value; });
    
    fetch(config.apiEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(function(res) { return res.json(); })
    .then(function(result) {
      if (result.success) {
        form.style.display = 'none';
        successDiv.style.display = 'block';
      } else {
        throw new Error(result.message || 'Une erreur est survenue');
      }
    })
    .catch(function(err) {
      errorDiv.textContent = err.message;
      errorDiv.style.display = 'block';
      btn.disabled = false;
      btn.textContent = config.buttonText;
    });
  });
})();
</script>`;
    
    return embedCode;
  };
  
  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success('Code copié dans le presse-papiers');
    setTimeout(() => setCopied(null), 2000);
  };
  
  const embedCode = generateEmbedCode();
  
  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Code className="w-6 h-6" style={{ color: theme?.colors?.accent }} />
            Formulaires d'intégration
          </h1>
          <p className="text-slate-500 mt-1">
            Générez des codes à intégrer sur vos sites pour collecter des leads
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration Panel */}
          <div className="space-y-6">
            {/* Form Type Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Type de formulaire
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFormType('callback')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formType === 'callback' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Phone className={`w-6 h-6 mb-2 ${formType === 'callback' ? 'text-green-600' : 'text-slate-400'}`} />
                    <div className="font-semibold text-slate-800">Demande de rappel</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Formulaire simple pour être rappelé
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setFormType('quote')}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formType === 'quote' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <FileText className={`w-6 h-6 mb-2 ${formType === 'quote' ? 'text-blue-600' : 'text-slate-400'}`} />
                    <div className="font-semibold text-slate-800">Demande de devis</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Formulaire avec description du projet
                    </div>
                  </button>
                </div>
              </CardContent>
            </Card>
            
            {/* Content Customization */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Contenu
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Titre du formulaire</Label>
                  <Input
                    value={formConfig.title}
                    onChange={(e) => setFormConfig({...formConfig, title: e.target.value})}
                    placeholder="Demande de rappel"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formConfig.description}
                    onChange={(e) => setFormConfig({...formConfig, description: e.target.value})}
                    placeholder="Laissez-nous vos coordonnées..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Texte du bouton</Label>
                  <Input
                    value={formConfig.buttonText}
                    onChange={(e) => setFormConfig({...formConfig, buttonText: e.target.value})}
                    placeholder="Envoyer"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Message de succès</Label>
                  <Textarea
                    value={formConfig.successMessage}
                    onChange={(e) => setFormConfig({...formConfig, successMessage: e.target.value})}
                    placeholder="Merci, nous vous recontacterons..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Fields & Style */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Champs & Style
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Couleur principale</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="color"
                        value={formConfig.primaryColor}
                        onChange={(e) => setFormConfig({...formConfig, primaryColor: e.target.value})}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formConfig.primaryColor}
                        onChange={(e) => setFormConfig({...formConfig, primaryColor: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Arrondi (px)</Label>
                    <Input
                      type="number"
                      value={formConfig.borderRadius}
                      onChange={(e) => setFormConfig({...formConfig, borderRadius: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <Label>Afficher le téléphone</Label>
                    <Switch
                      checked={formConfig.showPhone}
                      onCheckedChange={(v) => setFormConfig({...formConfig, showPhone: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Afficher l'email</Label>
                    <Switch
                      checked={formConfig.showEmail}
                      onCheckedChange={(v) => setFormConfig({...formConfig, showEmail: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Afficher le champ société</Label>
                    <Switch
                      checked={formConfig.showCompany}
                      onCheckedChange={(v) => setFormConfig({...formConfig, showCompany: v})}
                    />
                  </div>
                  {formType === 'quote' && (
                    <>
                      <div className="flex items-center justify-between">
                        <Label>Afficher type de projet</Label>
                        <Switch
                          checked={formConfig.showProjectType}
                          onCheckedChange={(v) => setFormConfig({...formConfig, showProjectType: v})}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Afficher délai de livraison</Label>
                        <Switch
                          checked={formConfig.showDeadline}
                          onCheckedChange={(v) => setFormConfig({...formConfig, showDeadline: v})}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between">
                    <Label>Afficher le message</Label>
                    <Switch
                      checked={formConfig.showMessage}
                      onCheckedChange={(v) => setFormConfig({...formConfig, showMessage: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Capturer l'URL de la page</Label>
                    <Switch
                      checked={formConfig.capturePageUrl}
                      onCheckedChange={(v) => setFormConfig({...formConfig, capturePageUrl: v})}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Preview & Code Panel */}
          <div className="space-y-6">
            {/* Preview */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Aperçu
                </CardTitle>
              </CardHeader>
              <CardContent className="bg-slate-100 p-6 rounded-lg">
                <div 
                  className="mx-auto"
                  style={{
                    maxWidth: '400px',
                    padding: '24px',
                    background: formConfig.backgroundColor,
                    borderRadius: `${formConfig.borderRadius}px`,
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                  }}
                >
                  <div className="text-xl font-semibold text-slate-800 mb-2">{formConfig.title}</div>
                  <div className="text-sm text-slate-500 mb-5">{formConfig.description}</div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet *</label>
                      <input
                        type="text"
                        className="w-full p-2.5 border border-slate-300 rounded-lg text-sm"
                        placeholder="Jean Dupont"
                        disabled
                      />
                    </div>
                    
                    {formConfig.showPhone && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone *</label>
                        <input
                          type="tel"
                          className="w-full p-2.5 border border-slate-300 rounded-lg text-sm"
                          placeholder="06 12 34 56 78"
                          disabled
                        />
                      </div>
                    )}
                    
                    {formConfig.showEmail && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input
                          type="email"
                          className="w-full p-2.5 border border-slate-300 rounded-lg text-sm"
                          placeholder="email@exemple.com"
                          disabled
                        />
                      </div>
                    )}
                    
                    {formConfig.showCompany && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Société</label>
                        <input
                          type="text"
                          className="w-full p-2.5 border border-slate-300 rounded-lg text-sm"
                          placeholder="Nom de votre société"
                          disabled
                        />
                      </div>
                    )}
                    
                    {formConfig.showProjectType && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type de projet *</label>
                        <select
                          className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white"
                          disabled
                        >
                          <option>Sélectionnez un type de projet</option>
                          {projectTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {formConfig.showDeadline && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Délai de livraison souhaité</label>
                        <select
                          className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white"
                          disabled
                        >
                          <option>Sélectionnez un délai</option>
                          {deadlineOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {formConfig.showMessage && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Votre message *</label>
                        <textarea
                          className="w-full p-2.5 border border-slate-300 rounded-lg text-sm min-h-[80px]"
                          placeholder="Décrivez votre projet..."
                          disabled
                        />
                      </div>
                    )}
                    
                    <button
                      className="w-full py-3 text-white font-semibold rounded-lg transition-opacity hover:opacity-90"
                      style={{ backgroundColor: formConfig.primaryColor }}
                      disabled
                    >
                      {formConfig.buttonText}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Generated Code */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  Code d'intégration
                </CardTitle>
                <CardDescription>
                  Copiez ce code et collez-le dans votre site web
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto max-h-[300px]">
                    <code>{embedCode}</code>
                  </pre>
                  <Button
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => handleCopy(embedCode, 'embed')}
                  >
                    {copied === 'embed' ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copié
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copier
                      </>
                    )}
                  </Button>
                </div>
                
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>💡 Conseil :</strong> Le formulaire capture automatiquement l'URL de la page 
                    où il est intégré. Les leads seront enregistrés avec la source "Site Agence".
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Link to full public quote page */}
            <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-cyan-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-emerald-700">
                  <FileText className="w-5 h-5" />
                  Page de devis complète
                </CardTitle>
                <CardDescription>
                  Une page dédiée avec brief complet qui crée automatiquement un prospect et un projet brouillon
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-white p-3 rounded-lg border text-sm text-slate-700 font-mono">
                    {backendUrl.replace('/api', '')}/devis
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(`${backendUrl.replace('/api', '')}/devis`, 'public_url')}
                  >
                    {copied === 'public_url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => window.open(`${backendUrl.replace('/api', '')}/devis`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Ouvrir
                  </Button>
                </div>
                <div className="mt-3 text-sm text-emerald-700">
                  <p><strong>Avantages :</strong></p>
                  <ul className="list-disc list-inside mt-1 space-y-1 text-emerald-600">
                    <li>Formulaire multi-étapes avec questions spécifiques au type de projet</li>
                    <li>Crée automatiquement un <strong>prospect</strong> avec la mention "brief complété"</li>
                    <li>Crée un <strong>projet en brouillon</strong> rattaché au prospect</li>
                    <li>Source : "Site Agence" avec URL de provenance</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EmbedFormsPage;
